// Small presentational primitives shared across pages (status pill, KPI tile, card, empty
// state). Every status is rendered as glyph + text + color — never color alone.

import type { CSSProperties, ReactNode } from 'react';
import { C, MONO, type StateToken } from '../lib/tokens';
import { CARD, chipS } from '../lib/styles';

/** A status pill: "● Live", "◆ Critical", etc. Optional prefix like "broker". */
export function Pill({ token, prefix }: { token: StateToken; prefix?: string }) {
  const [glyph, color, label] = token;
  return (
    <span style={chipS(color)}>
      {glyph} {prefix ? prefix + ' ' : ''}
      {label}
    </span>
  );
}

/** Inline mono "glyph label" used inside dense table cells. */
export function GlyphText({ token, text }: { token: StateToken; text?: string }) {
  const [glyph, color, label] = token;
  return (
    <span style={{ fontFamily: MONO, color }}>
      {glyph} {text ?? label}
    </span>
  );
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ ...CARD, ...style }}>{children}</div>;
}

export function CardHead({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid ' + C.ln2, flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}

export function H2({ children }: { children: ReactNode }) {
  return <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{children}</h2>;
}

export function Sub({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 11, color: C.dm, fontFamily: MONO }}>{children}</span>;
}

export function KpiTile({
  label,
  value,
  sub,
  valueColor,
  big,
  title,
}: {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
  big?: boolean;
  title?: string;
}) {
  return (
    <div style={{ ...CARD, padding: '10px 12px' }} title={title}>
      <div style={{ fontSize: 10, color: C.dm, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: big ? 17 : 16,
          fontWeight: 600,
          color: valueColor ?? C.tx,
          marginTop: 3,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      {sub != null && (
        <div style={{ fontSize: 10.5, color: C.dm, marginTop: 2, fontFamily: MONO }}>{sub}</div>
      )}
    </div>
  );
}

export function EmptyState({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ padding: '44px 20px', textAlign: 'center', ...CARD }}>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: C.dm, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

/** Money-cell color helper: profit green, loss red, else default. */
export function pnlColor(v: number | null | undefined): string {
  if (v == null) return C.gy;
  return v >= 0 ? C.gn : C.rd;
}
